import type { FileUIPart, ModelMessage, TextUIPart, ToolUIPart } from 'ai'
import {
  type ToolUIMessage,
  setToolMessageCallback,
} from '@/lib/agents/tool-ui'
import { saveAssistantMessage } from '@/lib/chats'
import { ABORT_MESSAGE } from '@/lib/constants/chat-constants'
import { buildFinalMessageParts, normalizeUsage } from './message-helpers'

type MessagePart = TextUIPart | FileUIPart | ToolUIPart

/**
 * Setup tool callback to collect tool parts.
 *
 * Handles two actions:
 * - "start": appends a new tool part (state: input-available)
 * - "update": replaces an existing tool part by toolCallId (state: output-*)
 */
export function setupToolCallback(
  assistantMsgId: string,
  toolParts: MessagePart[],
  chatId: string,
  updateMessages: (
    updater: (messages: ModelMessage[]) => ModelMessage[],
    chatId?: string
  ) => void
) {
  setToolMessageCallback((toolMessage: ToolUIMessage) => {
    const incomingParts = (toolMessage as unknown as { parts?: ToolUIPart[] })
      .parts
    if (!incomingParts?.length) return

    const action = toolMessage._action
    const incomingPart = incomingParts[0]

    if (action === 'update') {
      // Replace existing tool part in the collected array
      const idx = toolParts.findIndex(
        (p) =>
          'toolCallId' in p &&
          (p as ToolUIPart).toolCallId === incomingPart.toolCallId
      )
      if (idx !== -1) {
        toolParts[idx] = incomingPart
      } else {
        // Fallback: if start message was missed, just append
        toolParts.push(incomingPart)
      }
    } else {
      // "start" — append new tool part
      toolParts.push(incomingPart)
    }

    updateMessages((msgs) => {
      const updated = [...msgs]
      const lastMsg = updated[updated.length - 1]
      const lastMsgWithId = lastMsg as {
        id?: string
        role?: string
        parts?: MessagePart[]
      }
      if (
        lastMsg &&
        lastMsgWithId.id === assistantMsgId &&
        lastMsg.role === 'assistant'
      ) {
        const currentParts = [...(lastMsgWithId.parts || [])]

        if (action === 'update') {
          // Find and replace the existing tool part
          const partIdx = currentParts.findIndex(
            (p) =>
              'toolCallId' in p &&
              (p as ToolUIPart).toolCallId === incomingPart.toolCallId
          )
          if (partIdx !== -1) {
            currentParts[partIdx] = incomingPart
          } else {
            currentParts.push(incomingPart)
          }
        } else {
          currentParts.push(incomingPart)
        }

        updated[updated.length - 1] = {
          ...lastMsg,
          parts: currentParts,
        } as ModelMessage
      }
      return updated
    }, chatId)
  })
}

/**
 * Update assistant message content during streaming
 */
export function updateAssistantMessageContent(
  assistantMsgId: string,
  chunk: string,
  chatId: string,
  updateMessages: (
    updater: (messages: ModelMessage[]) => ModelMessage[],
    chatId?: string
  ) => void
) {
  updateMessages((msgs) => {
    const updated = [...msgs]
    const lastMsg = updated[updated.length - 1]
    const lastMsgWithId = lastMsg as {
      id?: string
      role?: string
      content?: unknown
    }
    if (
      lastMsg &&
      lastMsgWithId.id === assistantMsgId &&
      lastMsg.role === 'assistant'
    ) {
      const currentContent =
        typeof lastMsg.content === 'string' ? lastMsg.content : ''
      updated[updated.length - 1] = {
        ...lastMsg,
        content: currentContent + chunk,
      }
    }
    return updated
  }, chatId)
}

/**
 * Update assistant message with final content and parts
 */
export function updateAssistantMessageFinal(
  assistantMsgId: string,
  finalText: string,
  finalParts: MessagePart[],
  chatId: string,
  updateMessages: (
    updater: (messages: ModelMessage[]) => ModelMessage[],
    chatId?: string
  ) => void
) {
  updateMessages((msgs) => {
    const updated = [...msgs]
    const lastMsg = updated[updated.length - 1]
    const lastMsgWithId = lastMsg as { id?: string; role?: string }
    if (
      lastMsg &&
      lastMsgWithId.id === assistantMsgId &&
      lastMsg.role === 'assistant'
    ) {
      updated[updated.length - 1] = {
        ...lastMsg,
        content: finalText,
        parts: finalParts.length > 0 ? finalParts : undefined,
      } as ModelMessage
    }
    return updated
  }, chatId)
}

/**
 * Handle abort by updating message and saving to backend
 */
export async function handleAbortMessage(
  assistantMsgId: string,
  chatId: string,
  turnId: string | null,
  model: string,
  result: { totalUsage?: Promise<unknown> },
  toolParts: MessagePart[],
  isIncognito: boolean,
  updateMessages: (
    updater: (messages: ModelMessage[]) => ModelMessage[],
    chatId?: string
  ) => void
) {
  const stopMessage = ABORT_MESSAGE
  // Build final parts with stop message text + existing tool parts
  const finalParts = buildFinalMessageParts(stopMessage, toolParts)
  updateAssistantMessageFinal(
    assistantMsgId,
    stopMessage,
    finalParts,
    chatId,
    updateMessages
  )
  // Save stop message to backend
  if (!isIncognito && turnId && chatId) {
    const stopAssistantMsg = {
      id: assistantMsgId,
      role: 'assistant' as const,
      content: stopMessage,
      ...(finalParts.length > 0 && { parts: finalParts }),
    } as ModelMessage & { id: string }
    try {
      // Try to get usage if available, otherwise omit it
      const saveData: {
        chatId: string
        turnId: string
        message: ModelMessage & { id: string }
        model: string
      } = {
        chatId,
        turnId,
        message: stopAssistantMsg,
        model,
      }
      if (result.totalUsage) {
        try {
          const rawUsage = await result.totalUsage
          // Normalize usage to flat format for backend compatibility
          const usage = normalizeUsage(rawUsage)
          if (usage) {
            Object.assign(saveData, { usage })
          }
        } catch {
          // Usage not available when aborted, omit it
        }
      }
      await saveAssistantMessage(saveData)
    } catch (error) {
      console.error('Failed to save stop message to backend', error)
    }
  }
}
